import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import { Car, Check, X, Clock, Zap, RefreshCw, AlertCircle } from 'lucide-react';
import { getAuthToken } from '../../services/auth';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export function VehicleApprovals() {
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [rejectReason, setRejectReason] = useState({});
  const [showRejectInput, setShowRejectInput] = useState(null);

  const loadPending = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/vehicles/pending-approvals`, {
        credentials: 'include',
        headers: { 'Authorization': `Bearer ${getAuthToken()}` }
      });
      if (res.ok) setPending(await res.json());
    } catch (err) { console.error('Failed to load pending approvals:', err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadPending(); }, [loadPending]);

  const handleApprove = async (approvalId, plate) => {
    setActionLoading(approvalId);
    try {
      const res = await fetch(`${API_URL}/vehicles/approve/${approvalId}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Authorization': `Bearer ${getAuthToken()}` }
      });
      if (res.ok) {
        toast.success(`Vehicle ${plate} approved and added to allowlist`);
        loadPending();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Approval failed');
      }
    } catch { toast.error('Approval failed'); }
    finally { setActionLoading(null); }
  };

  const handleReject = async (approvalId, plate) => {
    const reason = rejectReason[approvalId] || 'Does not meet community guidelines';
    setActionLoading(approvalId);
    try {
      const res = await fetch(`${API_URL}/vehicles/reject/${approvalId}?reason=${encodeURIComponent(reason)}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Authorization': `Bearer ${getAuthToken()}` }
      });
      if (res.ok) {
        toast.success(`Vehicle ${plate} rejected`);
        setShowRejectInput(null);
        loadPending();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Rejection failed');
      }
    } catch { toast.error('Rejection failed'); }
    finally { setActionLoading(null); }
  };

  return (
    <Card className="shadow-lg border-0" data-testid="vehicle-approvals-card">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2">
          <Car className="w-5 h-5 text-amber-600" />
          Vehicle Approvals
          {pending.length > 0 && (
            <Badge className="bg-amber-500 text-white ml-1">{pending.length} pending</Badge>
          )}
        </CardTitle>
        <Button size="sm" variant="ghost" onClick={loadPending} className="h-8 w-8 p-0" data-testid="refresh-vehicle-approvals">
          <RefreshCw className="w-4 h-4" />
        </Button>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-center py-8 text-slate-500 text-sm">Loading...</div>
        ) : pending.length === 0 ? (
          <div className="text-center py-8">
            <Check className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
            <p className="text-sm text-slate-500">No pending vehicle approvals</p>
          </div>
        ) : (
          <div className="space-y-3">
            {pending.map((req) => (
              <div
                key={req.id}
                data-testid={`vehicle-approval-${req.id}`}
                className="p-4 bg-amber-50 rounded-lg border border-amber-200"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3 flex-1">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      req.vehicle_type === '2-wheeler' ? 'bg-amber-200' : 'bg-sky-200'
                    }`}>
                      <Car className={`w-5 h-5 ${
                        req.vehicle_type === '2-wheeler' ? 'text-amber-700' : 'text-sky-700'
                      }`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-bold text-sm">{req.make} {req.model}</p>
                        {req.is_ev && <Zap className="w-3.5 h-3.5 text-green-500" />}
                        <Badge variant="outline" className="text-xs">{req.vehicle_type}</Badge>
                        <Badge className="bg-amber-500 text-white text-[10px]">
                          <Clock className="w-2.5 h-2.5 mr-0.5" /> Pending
                        </Badge>
                      </div>
                      <p className="font-mono font-bold text-sm mt-0.5">{req.license_plate}</p>
                      <p className="text-xs text-slate-500 mt-1">
                        {req.color} | Requested by <span className="font-medium text-slate-700">{req.user_name}</span> (Villa {req.villa_number})
                      </p>
                      {req.phone_number && (
                        <p className="text-xs text-slate-400 mt-0.5">Phone: {req.phone_number}</p>
                      )}
                      <p className="text-[10px] text-slate-400 mt-1">
                        {new Date(req.created_at).toLocaleString()}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-col gap-1.5 shrink-0">
                    <Button
                      data-testid={`approve-vehicle-${req.id}`}
                      size="sm"
                      className="bg-emerald-600 hover:bg-emerald-700 text-xs h-8"
                      disabled={!!actionLoading}
                      onClick={() => handleApprove(req.id, req.license_plate)}
                    >
                      {actionLoading === req.id ? '...' : <><Check className="w-3.5 h-3.5 mr-1" /> Approve</>}
                    </Button>
                    <Button
                      data-testid={`reject-vehicle-${req.id}`}
                      size="sm"
                      variant="outline"
                      className="text-xs h-8 border-red-300 text-red-600 hover:bg-red-50"
                      disabled={!!actionLoading}
                      onClick={() => setShowRejectInput(showRejectInput === req.id ? null : req.id)}
                    >
                      <X className="w-3.5 h-3.5 mr-1" /> Reject
                    </Button>
                  </div>
                </div>
                {showRejectInput === req.id && (
                  <div className="mt-3 pt-3 border-t border-amber-200 flex gap-2">
                    <Input
                      data-testid={`reject-reason-${req.id}`}
                      placeholder="Rejection reason..."
                      value={rejectReason[req.id] || ''}
                      onChange={(e) => setRejectReason({ ...rejectReason, [req.id]: e.target.value })}
                      className="flex-1 h-8 text-xs"
                    />
                    <Button
                      data-testid={`confirm-reject-${req.id}`}
                      size="sm"
                      className="bg-red-600 hover:bg-red-700 text-xs h-8"
                      disabled={!!actionLoading}
                      onClick={() => handleReject(req.id, req.license_plate)}
                    >
                      <AlertCircle className="w-3 h-3 mr-1" /> Confirm
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
